/**
 * web/src/components/shared/Empty.tsx
 * Empty-state card matching `.empty` (handoff §Components §Empty state).
 * Dashed 1px border, soft bg, 8px radius, 64×24 padding, centered text.
 * 40×40 icon tile (white card on top), 14/600 title, 13/muted sub
 * (max 360px width per handoff).
 *
 * Used by: ApprovalQueue (no pending gates), Confirmations (no pending
 * receipts), WorkflowList (no workflows for the active filter).
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** Public props for Empty. */
export interface EmptyProps {
  icon?: ReactNode;
  title: string;
  description?: ReactNode;
  /** Optional CTA — typically a Button (e.g., "Trigger workflow"). */
  action?: ReactNode;
  className?: string;
}

/**
 * Renders an empty-state card with optional icon, title, description, and CTA.
 * Side effects: none — purely visual.
 */
export function Empty({ icon, title, description, action, className }: EmptyProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 px-6 py-16 bg-bg-soft border border-dashed border-border rounded-lg text-center',
        className,
      )}
    >
      {icon !== undefined && (
        <div className="w-10 h-10 grid place-items-center bg-bg border border-border rounded-md text-text-subtle">
          {icon}
        </div>
      )}
      <div className="text-[14px] font-semibold text-text">{title}</div>
      {description && (
        <div className="text-[13px] text-text-muted max-w-[360px]">{description}</div>
      )}
      {action}
    </div>
  );
}
